import {Graphics} from '@/services/GraphicService';
import {MapService, Markers} from '@/services/MapService';
import {createPlaceMarkerGraphic} from './PlaceMarkerGraphic.js';

/** @typedef {import('../GraphicService').GraphicData & PlaceMarkerData} PlaceMarkerGraphicData */

/** @typedef {(graphicExport: import('../Graphic').GraphicExport) => void} PlaceMarkerCallback */

/**
 * @param {PlaceMarkerCallback} callback A callback to be invoked with the created place marker data.
 * @return {function(): void} A function that cancels the place marker creation mode when invoked.
 */
function createNewPlaceMarker(callback) {
  // Prep the map for adding a new marker
  MapService.setCursor('crosshair');
  Markers.setAllClickable(false);
  Graphics.setAllClickable(false);

  // To exit create mode, revert the cursor and restore clickability.
  const exitCreateMode = () => {
    MapService.setCursor(null);
    Markers.setAllClickable(true);
    Graphics.setAllClickable(true);
    MapService.removeMapListener('createNewPlaceMarker_click', escapeListener);
    window.removeEventListener('keydown', escapeListener);
  };

  // If a user presses the escape key, exit create mode.
  const escapeListener = (event) => {
    if (event.key === 'Escape') {
      exitCreateMode();
    }
  };
  window.addEventListener('keydown', escapeListener, {once: true});

  // When a user clicks the map, create a new place marker at that position.
  MapService.addMapListener('createNewPlaceMarker_click', 'click', async (event) => {
    exitCreateMode();
    const placeMarker = createPlaceMarkerGraphic({position: event.latLng, featureType: 'placeMarker'});

    setTimeout(() => { // the original placemarker is destroyed as a new one is created when the feature is
      // added, but that takes a moment, so wait a moment to prevent flicker.
      placeMarker.setMap(null);
    }, 400);

    // Trigger the callback, effectively adding the new place marker to the shared map object
    if (!placeMarker.displayFields) {
      placeMarker.displayFields = [];
    }
    if (!placeMarker.comps) {
      placeMarker.comps = [];
    }

    const json = {...placeMarker.toJSON()};
    await callback(json);
  });

  // Return the exit for create mode so it can be called as a cancel callback
  return exitCreateMode;
}

export {createNewPlaceMarker};
